import { z } from 'zod';
import { brandedUuid } from './ids.ts';

/**
 * `web_search` (Jina) contracts — the typed shapes that cross the search seam
 * between the `@4f/agent` `web_search` tool (which composes quota → client →
 * truncate) and the backend `services/search/*` that fulfils it (the Jina client,
 * the per-user daily quota, and the `usage_events` write). Defined HERE (not
 * agent- or backend-local) so the tool's port, the backend service, and — later —
 * the frontend citation renderer all agree on ONE Zod-validated shape with branded
 * ids and no drift (mirrors media.ts).
 *
 * See docs/idea/websearch.md + docs/mvp-v1.0.0/phase-6-retention.
 */

/**
 * One search hit — the citation shape attached to the assistant message so the
 * client can render "sources". `snippet` is the short excerpt from the SERP
 * (`s.jina.ai`); the full cleaned page (when read) rides in {@link WebSearchPage}.
 */
export const WebSearchSource = z.object({
  title: z.string(),
  url: z.string(),
  snippet: z.string(),
});
export type WebSearchSource = z.infer<typeof WebSearchSource>;

/** A top result read in full — the cleaned, LLM-friendly page text from `r.jina.ai`. */
export const WebSearchPage = z.object({
  title: z.string(),
  url: z.string(),
  content: z.string(),
});
export type WebSearchPage = z.infer<typeof WebSearchPage>;

/** What the Jina client returns: the ranked hit list plus the top-N cleaned pages. */
export const WebSearchResults = z.object({
  sources: z.array(WebSearchSource),
  pages: z.array(WebSearchPage),
});
export type WebSearchResults = z.infer<typeof WebSearchResults>;

/**
 * What the tool hands the client — the run scope (for the `usage_events` write)
 * plus the query and read depth. `readTop` is already clamped by the tool's Zod
 * schema, so the client can trust it.
 */
export const WebSearchRequest = z.object({
  accountId: brandedUuid('AccountId'),
  userId: brandedUuid('UserId'),
  query: z.string(),
  /** How many of the top results to read in full (0–N); clamped tool-side. */
  readTop: z.number().int().nonnegative(),
});
export type WebSearchRequest = z.infer<typeof WebSearchRequest>;

/**
 * The per-user daily fair-use verdict from the search quota. `allowed` is the
 * gate; `used`/`limit` let the tool tell the model how much of the day's budget is
 * left. Separate from the api credit gate — this is a per-tool cap (websearch.md).
 *
 * `day` is the RECEIPT: the UTC calendar day whose bucket this call incremented.
 * A refund reverses THAT bucket, so a search that fails just after midnight gives
 * the consumption back to the day that took it, not to the fresh day's allowance
 * (same reason the credit gate's `Reservation` carries `reservedAt`).
 */
export interface SearchQuotaDecision {
  readonly allowed: boolean;
  /**
   * How many the bucket that DECIDED this call now holds — the served count normally,
   * or the (non-refundable) upstream-attempt count when the attempt ceiling is what
   * denied it. Informational only: `allowed` is the gate, `limit` is what the model
   * is told.
   */
  readonly used: number;
  readonly limit: number;
  /** The `YYYY-MM-DD` UTC day this consumption landed in — never a user-settable zone. */
  readonly day: string;
}
